import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CurrentPlanCard from "@/components/billing/CurrentPlanCard";
import PlanSelector from "@/components/billing/PlanSelector";
import PaymentMethodCard from "@/components/billing/PaymentMethodCard";
import CancelSection from "@/components/billing/CancelSection";
import CancelConfirmationModal from "@/components/billing/CancelConfirmationModal";
import { useBillingPlan } from "@/hooks/useBillingPlan";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

export default function BillingPage() {
  const navigate = useNavigate();
  const { plan: billingPlan, isLoading, isError } = useBillingPlan();
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Determine error type based on SWR error state
  const error = isError ? "Failed to load billing information. Please try again." : null;


  if (isLoading) {
    return (
      <div className={cn("min-h-screen", theme.colors.backgroundCream, "flex flex-col")}>
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="text-xl text-gray-600 dark:text-gray-300">Loading billing information...</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen", theme.colors.backgroundCream, "transition-colors duration-200 flex flex-col")}>
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-10">
          <div className="mb-10">
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Billing & Subscription</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Manage your subscription and payment information.
            </p>
          </div>

          {error ? (
            <div className={cn(theme.cards.base, "p-8 text-center")}>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Error</h2>
              <p className={cn("text-sm", theme.text.error)}>{error}</p>
            </div>
          ) : !billingPlan ? (
            <div className={cn(theme.cards.base, "p-8 text-center")}>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">No Active Subscription</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Subscribe to unlock all features and start using PodWise.
              </p>
              <button
                onClick={() => navigate("/paywall")}
                className={cn(theme.buttons.primary, "px-6 py-3 rounded-xl")}
              >
                Subscribe Now
              </button>
            </div>
          ) : (
            <div className="space-y-8">
              <CurrentPlanCard plan={billingPlan} />
              <PlanSelector />
              <PaymentMethodCard />
              <CancelSection plan={billingPlan} onCancel={() => setShowCancelModal(true)} />
            </div>
          )}

          {billingPlan && (
            <CancelConfirmationModal
              isOpen={showCancelModal}
              onClose={() => setShowCancelModal(false)}
            />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
